import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Lightweight floating popover anchored to a trigger element via the
 * trigger's `getBoundingClientRect()`. Use this when the built-in
 * `<Menu>` primitive isn't enough — e.g. for multi-select checkbox
 * filters where every click would otherwise dismiss the menu.
 *
 * The caller owns the open/closed state and the anchor rect. We just
 * portal the children into document.body, position the box under the
 * anchor, and close on outside-click / Escape / page scroll.
 *
 * Props:
 *   open     — boolean
 *   anchor   — DOMRect-shaped object (or null), usually captured via
 *              event.currentTarget.getBoundingClientRect()
 *   onClose  — () => void
 *   children — popover contents (rows, headers, dividers — your call)
 *   minWidth — defaults to 220
 *   align    — 'left' (default) anchors the popover's left edge to
 *              the trigger's left; 'right' flips so the popover's
 *              right edge aligns with the trigger's right
 */
export default function Popover({
  open,
  anchor,
  onClose,
  children,
  minWidth = 220,
  align = 'left',
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onScroll = () => onClose();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, onClose]);

  if (!open || !anchor) return null;
  const top = anchor.bottom + 4;
  const style = align === 'right'
    ? { right: Math.max(8, window.innerWidth - anchor.right) }
    : { left: Math.max(8, anchor.left) };

  return createPortal(
    <div
      ref={ref}
      role="menu"
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top,
        ...style,
        minWidth,
        zIndex: 1000,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 8,
        boxShadow: 'var(--shadow-md)',
        padding: 4,
        fontSize: 14.5,
        maxHeight: '60vh',
        overflowY: 'auto',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/**
 * Section header inside a Popover — small uppercase label, optional
 * right-side "Clear" button. Used to group filter dimensions.
 */
export function PopoverHeader({ title, onClear }) {
  return (
    <div
      style={{
        padding: '6px 8px 4px',
        fontSize: 11.5,
        fontWeight: 600,
        color: 'var(--text-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <span>{title}</span>
      {onClear && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClear(); }}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: 'var(--accent-text)',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 500,
          }}
        >Clear</button>
      )}
    </div>
  );
}

/** Visual hairline divider inside a popover. */
export function PopoverDivider() {
  return (
    <div
      style={{
        height: 1,
        background: 'var(--border-subtle)',
        margin: '4px 6px',
      }}
    />
  );
}

/**
 * Multi-select row: 14px checkbox + label. Used inside filter
 * popovers. Stops mousedown propagation so the parent Popover's
 * outside-click listener (mousedown-based) doesn't fire from inside
 * and close the popover during a toggle.
 */
export function CheckMenuItem({ checked, label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '7px 10px',
        border: 'none',
        background: hover ? 'var(--bg-hover)' : 'transparent',
        color: 'var(--text-1)',
        cursor: 'pointer',
        borderRadius: 5,
        textAlign: 'left',
        fontSize: 14,
        fontWeight: 500,
        transition: 'background .1s',
      }}
    >
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: 3,
          background: checked ? 'var(--accent)' : 'var(--bg-surface)',
          border: `1px solid ${checked ? 'var(--accent)' : 'var(--border-default)'}`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          flexShrink: 0,
          transition: 'background .12s, border-color .12s',
        }}
      >
        {checked && (
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
}

/**
 * Single-select row: shows a check glyph at left when selected. Used
 * inside sort popovers (radio-list pattern).
 */
export function RadioMenuItem({ selected, label, onClick }) {
  const [hover, setHover] = useState(false);
  const bg = selected
    ? 'var(--accent-soft)'
    : hover
      ? 'var(--bg-hover)'
      : 'transparent';
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '7px 10px',
        border: 'none',
        background: bg,
        color: selected ? 'var(--accent-text)' : 'var(--text-1)',
        cursor: 'pointer',
        borderRadius: 5,
        textAlign: 'left',
        fontSize: 14,
        fontWeight: 500,
        transition: 'background .1s',
      }}
    >
      <span
        style={{
          width: 12,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--accent-text)',
        }}
      >
        {selected && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <span>{label}</span>
    </button>
  );
}
